import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService
} from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { ApmErrorHandler, ApmModule, ApmService } from '@elastic/apm-rum-angular';
import { DesignSystemModule } from '@ukho/admiralty-angular';
import { routes } from './app.routes';
import { apiConfig, loginRequest, msalConfig, tokenRequest } from './auth-config';
import { errorLoggingInterceptor } from './interceptors/error-logging.interceptor';
import { ElasticApmService } from './services/elastic-apm.service';
import { AuthenticationService } from './services/authentication.service';
import { NavigationService } from './services/navigation.service';
import { GtmService } from './services/gtm.service';
import { BrowserStorageService } from './services/browser-storage.service';
import { BASE_PATH } from './shared/data-upload.token';
import { ELASTIC_APM_TOKEN } from './shared/elastic-apm.token';
import { environment } from '../environments/environment';

// Followed https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/configuration.md
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  // MSAL Angular v5 uses strict URL matching by default (anchored regex).
  // The v4 key (base URL without path) had pathname "/" which no longer matches
  // sub-paths like "/api/external/user/get-roles" under strict matching.
  // Adding "/*" wildcard ensures all paths under the API domain are protected.
  const protectedResourceMap = new Map([[`${apiConfig.webApi}/*`, [...tokenRequest.scopes]]]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })),
    provideHttpClient(withInterceptors([errorLoggingInterceptor]), withInterceptorsFromDi()),
    importProvidersFrom(ApmModule, DesignSystemModule.forRoot()),
    { provide: BASE_PATH, useValue: environment.dataUploadApiEndpoint },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    { provide: ELASTIC_APM_TOKEN, useValue: environment.elasticApmConfig },
    ElasticApmService,
    ApmService,
    {
      provide: ErrorHandler,
      useClass: ApmErrorHandler
    },
    AuthenticationService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    NavigationService,
    GtmService,
    BrowserStorageService,
    {
      provide: APP_INITIALIZER,
      useFactory: (elasticApmService: ElasticApmService) => () => elasticApmService.initialize(),
      deps: [ElasticApmService],
      multi: true
    }
  ]
};
